import "./base.js";
import "../collections/visit_type_rule_selections.js";
import "./visit_type_rule_selection.js";
import "../collections/verify_id_answers.js";
import "./verify_id_answer.js";

window.AppointmentBooking ??= {};
window.AppointmentBooking.Wizard ??= {};
window.AppointmentBooking.Wizard.Models ??= {};

AppointmentBooking.Wizard.Models.Booking = class Booking extends AppointmentBooking.Wizard.Models.Base {
  constructor() {
    super(...arguments);
    this.initialize = this.initialize.bind(this);
    this.handleSettingVisitTypeId = this.handleSettingVisitTypeId.bind(this);
    this.handleSettingValidatedVisitTypeId = this.handleSettingValidatedVisitTypeId.bind(this);
    this.handleSettingInsuranceUpdateResponse = this.handleSettingInsuranceUpdateResponse.bind(this);
    this.setValidAgeRange = this.setValidAgeRange.bind(this);
    this.setVisitTypeChosen = this.setVisitTypeChosen.bind(this);
    this.isVisitTypeInvalid = this.isVisitTypeInvalid.bind(this);
    this.isVisitTypeValid = this.isVisitTypeValid.bind(this);
    this.findValidVisitType = this.findValidVisitType.bind(this);
    this.validVisitTypes = this.validVisitTypes.bind(this);
    this.getSelectedVisitTypeRules = this.getSelectedVisitTypeRules.bind(this);
    this.hasCompletedMyChartLogin = this.hasCompletedMyChartLogin.bind(this);
    this.insuranceUpdateQuestionEnabled = this.insuranceUpdateQuestionEnabled.bind(this);
    this.insuranceReportedUnchanged = this.insuranceReportedUnchanged.bind(this);
    this.insuranceCollectionSkipped = this.insuranceCollectionSkipped.bind(this);
    this.hasInsuranceToReview = this.hasInsuranceToReview.bind(this);
    this.toValidationJSON = this.toValidationJSON.bind(this);
    this.toCreationJSON = this.toCreationJSON.bind(this);
    this.resetErrors = this.resetErrors.bind(this);
    this.resetPatient = this.resetPatient.bind(this);
  }

  // Coerce DOB to expected format
  static normalizeDateOfBirth(dateString) {
    if (dateString) {
      return moment(dateString).format("M/D/YYYY");
    }
  }

  static dateOfBirthMoment(dateString) {
    if (dateString) {
      return moment(dateString, ["MM/DD/YYYY", "M/D/YYYY"], true);
    }
  }

  static {
    this.prototype.relations = [
      {
        type: Backbone.HasMany,
        key: "visit_type_rule_selections",
        relatedModel: "AppointmentBooking.Wizard.Models.VisitTypeRuleSelection",
        collectionType: "AppointmentBooking.Wizard.Collections.VisitTypeRuleSelections",
      },
      {
        type: Backbone.HasMany,
        key: "verify_id_answers",
        relatedModel: "AppointmentBooking.Wizard.Models.VerifyIdAnswer",
        collectionType: "AppointmentBooking.Wizard.Collections.VerifyIdAnswers",
      },
    ];

    this.prototype.serverAttributes = [
      "returning_patient",
      "ehr_appointment_slot_id",
      "location_during_visit",

      "patient_id",
      "patient_id_type",
      "pcp_id",

      "first_name",
      "last_name",
      "date_of_birth",
      "last_4_ssn",
      "gender",
      "email",
      "primary_phone",
      "primary_phone_type",
      "secondary_phone",
      "secondary_phone_type",
      "street",
      "street_line2",
      "city",
      "zip_code",
      "state",
      "login_type",

      "payor_id",
      "insurance_name",
      "member_number",
      "group_number",
      "subscriber_id",
      "subscriber_name",
      "subscriber_date_of_birth",

      "comments",
      "platform",

      "referring_url",
      "referring_ui_component",
    ];
  }

  // Return a moment from a normalized date string
  dateOfBirthMoment() {
    const dob = this.get("date_of_birth");
    if (dob) {
      return this.constructor.dateOfBirthMoment(dob);
    }
  }

  defaults() {
    return {
      returning_patients_only: false,

      mychart_login_error: null,
      patient_id: null,
      patient_id_type: null,
      pcp_id: null,

      errors: [],
      error_messages: {},

      in_age_range: true,
      returning_patient: false,
      ehr_appointment_slot_id: null,

      // PROVIDED BY SLOT METADATA ENDPOINT
      visit_type_id: null,
      decision_tree_id: null,
      check_provider_license: false,

      provider: { licensed_locations: [], specialties: [] },
      address: {},
      visit_types: [],
      appointment_slot: {},
      slot_messages: [],
      provider_type_message: {},

      // PROVIDED BY DECISION TREE
      decision_tree_current_node_id: null,
      decision_tree_result: null,
      decision_tree_booking_metadata: {},
      decision_tree_age_range: null,
      switch_provider_group_ids: [],
      review_step_broadcasts: [],

      slot_suggester_shown: false,
      slot_suggester_success: false,
      reselection_reason: null,
      reselection_result: null,
      reselection_type: null,

      // PROVIDED BY MYCHART SIGN IN
      patient_proxies: [],

      // PROVIDED BY THE VALIDATION ENDPOINT
      validated_returning_patient: null,
      validated_visit_type_id: null,
      validated_ehr_appointment_slot_id: null,

      validated_provider: {},
      validated_address: {},
      validated_visit_types: [],
      validated_appointment_slot: {},

      validated_valid: null,
      validated_user_selected_slot: false,

      validated_verify_id_success_token: null,

      validated_invalid_patient_type_message_key: null,
      validated_invalid_patient_type_message_reason: null,
      validated_invalid_patient_type_message_contact_info: null,

      // CALCULATED when other values are set
      visit_type: null, // The currently selected visit type
      validated_visit_type: null, // The currently selected validated visit type
      valid_age_range: null,
      visit_type_chosen: null,
      physical_address: null,

      // USER RESPONSES

      // WELCOME
      decision_tree_enabled: null,
      welcome_complete: false,

      // VISIT
      location_during_visit: null,

      // SIGN IN
      login_type: null,

      // PATIENT
      first_name: null,
      last_name: null,
      date_of_birth: null,
      last_4_ssn: null,
      gender: null,
      email: null,
      primary_phone: null,
      primary_phone_type: "mobile",
      secondary_phone: null,
      secondary_phone_type: "home",
      street: null,
      street_line2: null,
      city: null,
      zip_code: null,
      state: null,

      // INSURANCE
      insurance_update_response: null, // 'unchanged', 'changed' or 'self_pay' when the insurance update question is enabled
      payor_id: null,
      insurance_name: null,
      member_number: null,
      group_number: null,
      subscriber_id: null,
      subscriber_name: null,
      subscriber_date_of_birth: null,
      subscriber_is_patient: true,

      // VERIFY ID
      verify_id_session_id: null,
      verify_id_questions: [],
      verify_id_question_generation_error: null,

      // CONFIRMATION
      comments: null,
      platform: null,

      // ANALYTICS
      referring_url: null,
      referring_ui_component: null,
    };
  }

  initialize() {
    // BEHAVIOUR

    this.on("setting:visit_type_id", this.handleSettingVisitTypeId);
    this.on("setting:validated_visit_type_id", this.handleSettingValidatedVisitTypeId);

    this.on("setting:visit_type_id setting:decision_tree_age_range", this.setValidAgeRange);

    this.on("setting:insurance_update_response", this.handleSettingInsuranceUpdateResponse);

    return this.on(
      "setting:decision_tree_enabled setting:welcome_complete setting:appointment_slot",
      this.setVisitTypeChosen,
    );
  }

  // EVENT HANDLERS

  handleSettingVisitTypeId(key, value, attrsToSet) {
    // Always include the currently selected visit type as a context variable because we use it so often
    const visitTypes = _.has(attrsToSet, "visit_types") ? attrsToSet.visit_types : this.get("visit_types");
    attrsToSet["visit_type"] = _.findWhere(visitTypes, { id: value });

    // If we're changing the visit type to a blank value, record the last known good value
    // Otherwise clear the last known good value if we're changing to a non-blank value
    if (this.get("visit_type_id") != null && value == null) {
      const last_valid_visit_type_id = this.get("visit_type_id");
      const last_valid_visit_type = _.findWhere(visitTypes, { id: last_valid_visit_type_id });
      attrsToSet["last_valid_visit_type"] = last_valid_visit_type;
      return (attrsToSet["last_valid_visit_type_id"] =
        last_valid_visit_type != null ? last_valid_visit_type.id : undefined);
    } else if (this.get("visit_type_id") == null && value != null) {
      attrsToSet["last_valid_visit_type"] = null;
      return (attrsToSet["last_valid_visit_type_id"] = null);
    }
  }

  handleSettingValidatedVisitTypeId(key, value, attrsToSet) {
    // Always include the currently selected visit type as a context variable because we use it so often
    const visitTypes = _.has(attrsToSet, "validated_visit_types")
      ? attrsToSet.validated_visit_types
      : this.get("validated_visit_types");
    return (attrsToSet["validated_visit_type"] = _.findWhere(visitTypes, { id: value }));
  }

  // Each response starts from a clean slate so insurance entered under a previous answer is never submitted.
  // Self-pay maps onto the existing no-insurance payor so downstream handling matches a manual no-insurance selection.
  handleSettingInsuranceUpdateResponse(key, value, attrsToSet) {
    if (value == null) {
      return;
    }

    const defaults = this.defaults();
    const insuranceAttributes = [
      "payor_id",
      "insurance_name",
      "member_number",
      "group_number",
      "subscriber_id",
      "subscriber_name",
      "subscriber_date_of_birth",
      "subscriber_is_patient",
    ];
    insuranceAttributes.forEach((attribute) => (attrsToSet[attribute] = defaults[attribute]));

    if (value === "self_pay") {
      attrsToSet["payor_id"] = AppointmentBooking.config.special_payors.no_insurance;
    }
  }

  setValidAgeRange(key, value, attrsToSet) {
    let maximum_age;
    let minimum_age;
    let validAgeRange;
    const decisionTreeAgeRange = attrsToSet["decision_tree_age_range"] || this.get("decision_tree_age_range");
    const visitType = attrsToSet["visit_type"] || this.get("visit_type");

    if (decisionTreeAgeRange) {
      minimum_age = decisionTreeAgeRange.minimumAge;
      maximum_age = decisionTreeAgeRange.maximumAge;
    } else if (visitType != null ? visitType.verify_age_range : undefined) {
      ({ minimum_age } = visitType);
      ({ maximum_age } = visitType);
    }

    if (minimum_age != null || maximum_age != null) {
      validAgeRange = { minimum_age, maximum_age };
    } else {
      validAgeRange = null;
    }

    return (attrsToSet["valid_age_range"] = validAgeRange);
  }

  setVisitTypeChosen(key, value, attrsToSet) {
    const decision_tree_enabled = _.has(attrsToSet, "decision_tree_enabled")
      ? attrsToSet.decision_tree_enabled
      : this.get("decision_tree_enabled");
    const welcome_complete = _.has(attrsToSet, "welcome_complete")
      ? attrsToSet.welcome_complete
      : this.get("welcome_complete");
    const address = _.has(attrsToSet, "address") ? attrsToSet.address : this.get("address");
    const ehr_appointment_slot_id = _.has(attrsToSet, "ehr_appointment_slot_id")
      ? attrsToSet.ehr_appointment_slot_id
      : this.get("ehr_appointment_slot_id");
    const appointment_slot = _.has(attrsToSet, "appointment_slot")
      ? attrsToSet.appointment_slot
      : this.get("appointment_slot");

    // Defer setting visit_type_chosen and physical_address until we have the correct appointment_slot data
    if (
      !ehr_appointment_slot_id ||
      ehr_appointment_slot_id !== (appointment_slot != null ? appointment_slot.id : undefined)
    ) {
      return;
    }

    attrsToSet["visit_type_chosen"] = !decision_tree_enabled || welcome_complete;
    return (attrsToSet["physical_address"] = attrsToSet["visit_type_chosen"]
      ? appointment_slot.in_person
      : !address.virtual);
  }

  // HELPER METHODS

  isVisitTypeInvalid() {
    return this.get("visit_type") && !this.isVisitTypeValid();
  }

  isVisitTypeValid() {
    return _.some(this.validVisitTypes(), { id: this.get("visit_type_id") });
  }

  findValidVisitType(predicate) {
    return _.findWhere(this.validVisitTypes(), predicate);
  }

  validVisitTypes() {
    return this.get("visit_types").filter((visitType) => {
      if (this.get("returning_patient")) {
        return visitType.valid_for_returning_patient;
      } else if (!this.get("returning_patients_only")) {
        return visitType.valid_for_new_patient;
      }
    });
  }

  getSelectedVisitTypeRules() {
    return this.get("visit_type")?.rules || [];
  }

  hasCompletedMyChartLogin() {
    return this.get("login_type") === "mychart" && this.get("patient_id") != null;
  }

  insuranceUpdateQuestionEnabled() {
    return !!AppointmentBooking.config.ask_insurance_update_question;
  }

  insuranceReportedUnchanged() {
    return this.insuranceUpdateQuestionEnabled() && this.get("insurance_update_response") === "unchanged";
  }

  insuranceCollectionSkipped() {
    const visitType = this.get("visit_type");

    return (visitType && !visitType.requires_insurance_info) || this.hasCompletedMyChartLogin();
  }

  // "Same as last visit" has nothing to recap — the details stay whatever the EHR already holds
  hasInsuranceToReview() {
    if (!this.insuranceUpdateQuestionEnabled()) {
      return false;
    }
    if (this.insuranceCollectionSkipped()) {
      return false;
    }

    return ["changed", "self_pay"].includes(this.get("insurance_update_response"));
  }

  toValidationJSON() {
    const bookingJSON = this.slice(...Array.from(this.serverAttributes || []));

    bookingJSON.verify_id_session_id = this.get("verify_id_session_id");
    bookingJSON.verify_id_answer_indices = this.get("verify_id_answers").pluck("index");

    // Send existing success token in case user moved back to Verify ID page before continuing again
    bookingJSON.verify_id_success_token = this.get("validated_verify_id_success_token");

    const json = {
      ehr_appointment: bookingJSON,
    };

    return json;
  }

  toCreationJSON() {
    const bookingJSON = this.slice(...Array.from(this.serverAttributes || []));

    bookingJSON.returning_patient = this.get("validated_returning_patient");
    bookingJSON.ehr_appointment_slot_id = this.get("validated_ehr_appointment_slot_id");

    bookingJSON.verify_id_session_id = this.get("verify_id_session_id");
    bookingJSON.verify_id_success_token = this.get("validated_verify_id_success_token");

    bookingJSON.selected_visit_type_rule_option_ids = this.get("visit_type_rule_selections").pluck("option_id");

    bookingJSON.decision_tree_booking_metadata = JSON.stringify(this.get("decision_tree_booking_metadata"));

    const json = {
      ehr_appointment: bookingJSON,
      sparklehp: this.get("sparklehp"), // Honeypot spam prevention
    };

    return json;
  }

  resetErrors() {
    const defaults = this.defaults();
    return this.set({ errors: defaults.errors, error_messages: defaults.error_messages });
  }

  resetPatient() {
    const defaults = this.defaults();
    return this.set({
      patient_id: defaults.patient_id,
      patient_id_type: defaults.patient_id_type,
      patient_proxies: defaults.patient_proxies,
    });
  }
};
